import { mapValues, pick } from 'lodash';
import { Nullable } from "../../../types/utils";
import client from "../client"
import { IPaginatedResponse, TPaginatedCollectionResponse, TParamsWithPagination } from '../helpers/types';
import { parseValidationError } from '../responseHandlers';
import { TOrderProductData } from "./OrderProduct";

// TODO: Remove this constant when the backend is updated
const ORDER_VOLUME_HARD_LIMIT_DAILY = 4294967296000; // 4000 GiB

export enum OrderStatus {
  DRAFT = "draft",
  SUBMITTED = "submitted",
  ACCEPTED = "accepted",
  REJECTED = "rejected",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
  ACTION_REQUIRED = "action_required"
}

export enum OrderCategory {
  WEB = 'web',
  REALTIME = 'realtime',
  ARCHIVE = 'archive',
  EU_CUTOUT = 'european_cutout'
}

export type TApiOrder<C = OrderCategory> = {
  id: number;
  ccp_organisation: number | null;
  organisation_id: number | null;
  category: C;
  status: OrderStatus,
  customer: string;
  name: string;
  description: string;
  has_legs: boolean;
  created: string;
  updated: string;
}

export type QuoteProductLeg = {
  step?: Array<string>;
  grid?: string;
};

export type TProductCost = {
  mars_requests: Array<string>;
  average_daily_fields: number;
  average_daily_volume: number;
  yearly_fields: number;
  yearly_volume: number;
}

export interface IEuCutOutProduct {
  param: Array<{
    value: string;
    subset_id: number;
  }>,
  high_frequency: boolean;
}

export interface TOrderCostV1 {
  version: 'v1';
  data: {
    total_cost?: {
      hf_information_cost: Nullable<number>,
      handling_cost: number;
      total_cost: number;
      epu: Nullable<number>;

      average_daily_fields: number;
      yearly_fields: number;

      // volume: Nullable<number>;
      average_daily_volume: number;
      yearly_volume: number;

      discount_epu: Nullable<number>;
      information_cost: Nullable<number>;
      max_charge: boolean;
    },
    per_product?: Array<TProductCost>;
    error?: TOrderCostError;
  }
}

export type TOrderCostError = {
  message: string;
  traceback: string;
  type: string;
}

export interface TOrderCostV2 {
  version: 'v2';
  data: {
    ecquote_version: Nullable<string>;
    total_cost?: {
      epu: number;
      field_count: number;
      average_daily_fields: number;
      yearly_fields: number;
      // volume: number;
      average_daily_volume: number;
      yearly_volume: number;

      information_cost: number;
      information_cost_after_discount: number;
      information_cost_hf?: number;
      information_cost_standard?: number;

      service_charge: number;
      service_charge_after_discount: number;

      total_cost: number;
      max_charge: boolean;
    },
    per_product?: Array<TProductCost>;
    error?: TOrderCostError;
  }
}

export interface TOrderCostV3 extends Omit<TOrderCostV2, 'version'> {
  version: 'v3'
}

export interface TOrderCostV4 extends Omit<TOrderCostV3, 'version'> {
  version: 'v4'
}

export interface TOrderCostV5 extends Omit<TOrderCostV4, 'version'> {
  version: 'v5'
}

export interface TOrderCostV6 {
  version: 'v6';
  data: {
    ecquote_version: Nullable<string>;
    total_cost?: {
      average_daily_volume: number,
      average_daily_fields: number,
      yearly_volume: number,
      yearly_fields: number,
      volume_band_cost: number,
      service_package_cost: number,
      service_charge: number
    },
    per_product?: Array<TProductCost>;
    error?: TOrderCostError;
  }
}

export type TOrderCost = TOrderCostV1 | TOrderCostV2 | TOrderCostV3 | TOrderCostV4 | TOrderCostV5 | TOrderCostV6;

export type TOrderExpiredCode = 'unsupported_costing_version' | '50r1_scda_scwv_enfo_waef_cf';

export type TApiOrderDetails<C = OrderCategory> = TApiOrder<C> & {
  email: string;
  status_note: string;
  start_date: Nullable<string>;
  small_business: Nullable<boolean>;
  research: Nullable<boolean>;
  volume_band_id: Nullable<number>;
  service_package_id: Nullable<number>;
  data_deliverer_organisation_id: Nullable<number>;
  expired_at: Nullable<string>;
  expired_code: Nullable<TOrderExpiredCode>;
  cost: TOrderCost;
}

export interface OrderCostingVersion {
  version: TOrderCost['version'];
  title: string;
  description: string;
  epu: number;
  service_charge_model_id: number;
  latest: boolean;
  supported: boolean;
  max_average_daily_volume: number;
  ecquote_version: Nullable<string>;
}

export type TOrderCreateParams = {
  name: string;
  description?: string;
  category: OrderCategory.WEB;
  folder_id?: Nullable<number>;
  research?: Nullable<boolean>;
  start_date: string;
} | {
  name: string;
  description?: string;
  category: OrderCategory.ARCHIVE
  folder_id?: Nullable<number>;
  research?: Nullable<boolean>;
  start_date: string;
} | {
  name: string;
  description?: string;
  category: OrderCategory.REALTIME;
  products: Array<TOrderProductData<OrderCategory.REALTIME>>;
  folder_id?: Nullable<number>;
  research?: Nullable<boolean>;
  start_date: string;
} | {
  name: string;
  description?: string;
  category: OrderCategory.EU_CUTOUT,
  products: Array<TOrderProductData<OrderCategory.EU_CUTOUT>>;
  folder_id?: Nullable<number>;
  research?: Nullable<boolean>;
  start_date: string;
}

export type TOrderSubmitParams = {
  status_note: string;
  research: boolean;
  service_package_id?: number;
}

export interface TOrderListParams {
  organisation_id?: number;
  status?: string;
  category?: string;
  search?: string
  folder_id?: Nullable<number>;
}

export type TOrderUpdateParams = Partial<TOrderCreateParams>;

type TOrderIdParam = string | number;

const allowedParams = (params: TOrderUpdateParams): TOrderUpdateParams => {
  return pick(
    params,
    'name', 'description', 'category', 'products', 'folder_id', 'start_date', 'research'
  );
}

const convertNullToString = (value: any) => {
  if (value === null) {
    return 'null';
  }

  return value;
}

const create = (params: TOrderCreateParams): Promise<TApiOrderDetails> =>
  client.post('/order/', allowedParams(params)).then(response => response.data);

const update = (id: TOrderIdParam, params: TOrderCreateParams): Promise<TApiOrderDetails> =>
  client.put(`/order/${id}/`, allowedParams(params)).then(response => response.data);

const patch = (id: TOrderIdParam, params: TOrderUpdateParams): Promise<TApiOrderDetails> =>
  client.patch(`/order/${id}/`, allowedParams(params))
    .then(response => response.data)
    .catch(parseValidationError);

const list = <T extends TParamsWithPagination<TOrderListParams>>(params?: T):
  Promise<IPaginatedResponse<TApiOrder>> =>
  client.get('/order/', { params: mapValues(params, convertNullToString) }).then(response => response.data);

const listCCPOrders = <T extends TParamsWithPagination<TOrderListParams>>(params?: T):
  Promise<IPaginatedResponse<TApiOrder>> =>
  client.get('/ccp-order/', { params }).then(response => response.data);

const listPublicOrders = <T extends TParamsWithPagination<TOrderListParams>>(params?: T):
  Promise<IPaginatedResponse<TApiOrder>> =>
  client.get('/public-order/', { params }).then(response => response.data);

const fetch = (id: TOrderIdParam): Promise<TApiOrderDetails> =>
  client.get(`/order/${id}/`).then(response => response.data);

const remove = (id: TOrderIdParam) => client.delete(`order/${id}/`);

const submit = (id: TOrderIdParam, params: TOrderSubmitParams): Promise<TApiOrderDetails> =>
  client.post(`order/${id}/submit/`, params).then(response => response.data);

const cancel = (id: TOrderIdParam): Promise<TApiOrderDetails> =>
  client.post(`order/${id}/cancel/`).then(response => response.data);

const approve = (id: TOrderIdParam): Promise<TApiOrderDetails> =>
  client.post(`order/${id}/accept/`).then(response => response.data);

const reject = (id: TOrderIdParam, reason: string): Promise<TApiOrderDetails> =>
  client.post(`order/${id}/reject/`).then(response => response.data);

const recalculate = (id: TOrderIdParam): Promise<TApiOrderDetails> =>
  client.put(`order/${id}/recalculate/`).then(response => response.data).then(order => {
    return order;
  });

const setCostingVersion = (id: TOrderIdParam, version: TOrderCost['version']): Promise<TApiOrderDetails> =>
  client.put(`order/${id}/set-costing-version/`, { version }).then(response => response.data);

const costingVersions = (id: TOrderIdParam): Promise<Array<OrderCostingVersion>> => {
  return client.get<Array<OrderCostingVersion>>(`order/${id}/costing-versions/`).then(response => response.data);
}

const moveToFolder = (id: TOrderIdParam, folderId: number | null): Promise<TApiOrderDetails> =>
  client.put(`/order/${id}/move/`, {
    folder_id: folderId
  }).then(response => response.data);

const duplicate = (id: TOrderIdParam, folderId?: Nullable<number>): Promise<TApiOrderDetails> =>
  client.put(`/order/${id}/duplicate/`, {
    folder_id: folderId
  }).then(response => response.data);

const fetchDisseminationRequest = (id: TOrderIdParam) =>
  client.get(`order/${id}/diss/`, {
    headers: {
      'Content-Type': 'text/plain'
    }
  }).then(response => response.data)

export {
  create,
  update,
  patch,
  fetch,
  remove,
  cancel,
  approve,
  reject,
  recalculate,
  costingVersions,
  setCostingVersion,
  moveToFolder,
  duplicate,
  list,
  listCCPOrders,
  listPublicOrders,
  submit,
  fetchDisseminationRequest
}
